import { useTranslation } from 'react-i18next'
import { Checkbox } from 'primereact/checkbox'
import Button from '../../../components/ui/Button'
import { rowActionLabel } from '../../../lib/deleteLabel'
import { TODO_TASK_STATUS, type TodoTask } from '../types'

interface Props {
  task: TodoTask
  busy: boolean
  onToggle: (task: TodoTask, completed: boolean) => void
  onDelete: (task: TodoTask) => void
  /** 123 — maddenin Dosyalar pop-up'ını açar. */
  onFiles: (task: TodoTask) => void
  /** 124 — maddenin Yorum pop-up'ını açar. */
  onComment: (task: TodoTask) => void
  /**
   * Kısıtlı Yetkili asistan kipi (126). `true` iken **tamamlama kutusu pasifleşir ve silme düşer**;
   * yorum/dosya düğmeleri DURUR — ikisi de okuma penceresi açar, pop-up'lar kendi içinde
   * salt-okumaya geçer.
   */
  readOnly?: boolean
}

/**
 * Notlarım madde satırı (113) — kutu + metin + yorum (124) + yorum/dosya/sil düğmeleri.
 *
 * <p><b>Mobil öncelikli:</b> silme düğmesi satırın içinde ve her zaman görünürdür (hover'da beliren
 * bir düğme dokunmatikte hiç görünmez). Hedef boyutu `todo.css`'te dokunma için büyütülür.
 *
 * <p>Kutunun adını <b>yandaki `<label htmlFor>` verir</b>; ayrıca `aria-label` konmaz — iki
 * adlandırma birbiriyle yarışır ve ekran okuyucuda hangisinin okunacağı tarayıcıya kalırdı.
 *
 * <p><b>Salt-okuma hâli KALKTI</b> (30.09.2026): satır artık listeyi gören herkese aynı çizilir,
 * çünkü paylaşılan kullanıcı da maddeyi tamamlayıp silebiliyor. Önceki `owned` prop'u ve ona bağlı
 * `disabled`/gizleme mantığı kaldırıldı — istemcide bırakılacak bir koşul kalmadığı için prop'un
 * kendisi de kaldırıldı (kullanılmayan bir bayrak, ileride yanlış bir kapı sanılırdı).
 */
export default function TodoTaskRow({
  task,
  busy,
  onToggle,
  onDelete,
  onFiles,
  onComment,
  readOnly = false,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const completed = task.todoTaskStatusId === TODO_TASK_STATUS.COMPLETED
  const hasAttachments = task.attachmentCount > 0
  /** Ek sayısı varsa erişilebilir ad da onu söyler — rozet görsel, ad sesli karşılığıdır. */
  const filesLabel = hasAttachments
    ? rowActionLabel(t, 'todo.filesWithAttachments', task.name, { count: task.attachmentCount })
    : rowActionLabel(t, 'todo.files.open', task.name)
  /**
   * Salt-okumada etiket **"görüntüle"** olur. "Yorumu düzenle" diyen bir düğmenin düzenlemeye
   * götürmemesi, Kısıtlı Yetkinin kapattığı diğer yüzeylerle aynı yanılgıyı üretirdi (FR-019).
   */
  const commentLabelKey = (() => {
    if (readOnly) return 'todo.comment.view'
    return task.description ? 'todo.comment.edit' : 'todo.comment.add'
  })()
  const commentLabel = rowActionLabel(t, commentLabelKey, task.name)
  /**
   * Salt-okumada **boş** yorum/dosya düğmesi hiç çizilmez: açacağı pencerede okunacak bir şey yok
   * ve "ekle" çağrışımı yapan bir düğme bırakmak yanıltıcı olurdu. Doluysa durur — içeriği
   * görmek okumadır.
   */
  const showComment = !readOnly || Boolean(task.description)
  const showFiles = !readOnly || hasAttachments

  return (
    <li className={completed ? 'todo-task todo-task--done' : 'todo-task'}>
      {/* Kutu Kısıtlı Yetkide GİZLENMEZ, PASİFLEŞİR: maddenin tamamlanmış olup olmadığı bir
          BİLGİDİR ve okunabilmelidir — kutuyu kaldırmak o bilgiyi de kaldırırdı. */}
      <Checkbox
        inputId={`todo-task-${task.id}`}
        checked={completed}
        disabled={busy || readOnly}
        onChange={(e) => onToggle(task, Boolean(e.checked))}
      />
      <label className="todo-task-text" htmlFor={`todo-task-${task.id}`}>
        {task.name}
      </label>
      {/* 124 — Yorum kolonu: tam metin, sarılır (kırpılmaz). Kolon başlığı telefonda gizlendiği ve
          ekran okuyucu başlığı satırla ilişkilendirmediği için metnin önünde gizli bir "Yorum:" durur. */}
      <span className="todo-task-comment">
        {task.description && (
          <>
            <span className="ui-sr-only">{t('todo.comment.label')}: </span>
            {task.description}
          </>
        )}
      </span>
      {showComment && (
        <Button
          icon="pi pi-comment"
          rounded
          text
          severity={task.description ? undefined : 'secondary'}
          disabled={busy}
          className="todo-task-comment-btn"
          aria-label={commentLabel}
          title={commentLabel}
          onClick={() => onComment(task)}
        />
      )}
      {/* 123 — Dosyalar. Eki olan maddede ikon vurgulu çizilir ve üstünde dosya sayısı rozeti durur;
          ayrım yalnız görselde kalmasın diye erişilebilir ad da sayıyı söyler ("… dosyaları (2
          dosya)"). Rozet düğmenin KARDEŞİ ve `aria-hidden` — gerekçesi liste kartındaki paylaşım
          rozetiyle aynı (TodoListCard): düğmenin içindeki metin ada karışırdı. */}
      {showFiles && (
        <span className="todo-task-files-action">
          <Button
            icon="pi pi-paperclip"
            rounded
            text
            severity={hasAttachments ? undefined : 'secondary'}
            disabled={busy}
            className={hasAttachments ? 'todo-task-files todo-task-files--has' : 'todo-task-files'}
            aria-label={filesLabel}
            title={filesLabel}
            onClick={() => onFiles(task)}
          />
          {/* Sıfırsa rozet HİÇ çizilmez — "0" yazan bir rozet bilgi taşımaz. */}
          {hasAttachments && (
            <span className="todo-files-badge" aria-hidden="true">
              {task.attachmentCount}
            </span>
          )}
        </span>
      )}
      {!readOnly && (
        <Button
          icon="pi pi-trash"
          rounded
          text
          severity="danger"
          disabled={busy}
          className="todo-task-delete"
          aria-label={rowActionLabel(t, 'todo.deleteTask', task.name)}
          title={rowActionLabel(t, 'todo.deleteTask', task.name)}
          onClick={() => onDelete(task)}
        />
      )}
    </li>
  )
}
